import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';

import { DataRepository } from '@core/data/data-repository';
import { UsersAccessApiActions, UsersAccessPageActions } from './users-access.actions';

/** Loads users, roles, the access matrix, and the position catalog together. */
@Injectable()
export class UsersAccessEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersAccessPageActions.entered),
      switchMap(() =>
        forkJoin({
          users: from(this.repo.listUsers()),
          roles: from(this.repo.listRoles()),
          accessMatrix: from(this.repo.listAccessMatrix()),
          positionCatalog: from(this.repo.listPositionCatalog()),
        }).pipe(
          map((r) => UsersAccessApiActions.loadSuccess(r)),
          catchError((err: unknown) =>
            of(UsersAccessApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /** Persist matrix edits so the change survives reloads and reaches the sidebar/guard. */
  readonly persistMatrix$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersAccessPageActions.matrixSaved),
        switchMap(({ accessMatrix }) =>
          from(this.repo.saveAccessMatrix(accessMatrix)).pipe(catchError(() => of(null))),
        ),
      ),
    { dispatch: false },
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
